/**
 * Editor State Storage Utility
 *
 * Manages persistent storage of editor drafts in LocalStorage.
 *
 * Key namespaces
 *   cellcog_editor_chat_{draftId}_v2   per-conversation draft. draftId is the chat id, or the
 *                                      EMPLOYEE id on employee routes (see LexicalMessageInput).
 *   cellcog_editor_new_chat_v1         the /new page draft (single scope — unaffected by _v2).
 *   cellcog_editor_metadata_v1         advisory key registry; rebuilt from a prefix scan.
 *
 * Why _v2: every _v1 conversation draft is presumed corrupt. A stale editor reference used to
 * write chat A's text under chat B's key, so one draft could end up duplicated across every
 * conversation the user visited (and legacy pre-CEL-604 keys were scoped to a shift id that no
 * longer exists). cleanupStaleDrafts() sweeps the whole _v1 namespace once, on app init.
 *
 * INVARIANTS this module guarantees for its callers:
 *   - Empty editor state in  ==  key removed. Never a stored empty draft.
 *   - Drafts are collectable by PREFIX SCAN, never dependent on the metadata registry.
 *   - Bounded: <= MAX_DRAFT_AGE_MS old and <= MAX_DRAFTS keys.
 */

// Storage version for schema compatibility (payload schema — not the key namespace)
const STORAGE_VERSION = 1;

// Storage keys
const NEW_CHAT_KEY = 'cellcog_editor_new_chat_v1';
const CHAT_DRAFT_PREFIX = 'cellcog_editor_chat_';
const CHAT_DRAFT_SUFFIX = '_v2';
const LEGACY_CHAT_DRAFT_SUFFIX = '_v1';
const METADATA_KEY = 'cellcog_editor_metadata_v1';
const PROJECTS_CACHE_KEY = 'cellcog_projects_cache_v1';

// Cleanup configuration
const MAX_DRAFT_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours for projects cache
const MAX_DRAFTS = 50; // hard cap on stored conversation drafts (oldest evicted first)

/**
 * Build the storage key for a conversation draft.
 * Exported so tests and the cleanup sweep speak the same language as the writers.
 */
export const chatDraftKey = (chatId) => `${CHAT_DRAFT_PREFIX}${chatId}${CHAT_DRAFT_SUFFIX}`;

/**
 * Draft scopes whose conversation was DELETED in this page session. Writes to them are refused.
 *
 * Needed because deleting the chat you are currently viewing is a race: the CHAT_DELETED handler
 * purges the key, then navigates away, and the composer's unmount flush would immediately re-create
 * the draft under an id nothing can ever open again — a permanent orphan.
 *
 * In-memory and session-scoped on purpose: that is exactly as long as a live composer can still
 * flush. After a reload no composer will ever mount for a deleted conversation.
 */
const purgedDraftIds = new Set();

/**
 * Check if editor state has actual content
 */
const hasContent = (editorState) => {
  if (!editorState || !editorState.root || !editorState.root.children) {
    return false;
  }

  // Check if root has any children
  if (!Array.isArray(editorState.root.children) || editorState.root.children.length === 0) {
    return false;
  }

  // Check if children have actual content (not just empty paragraphs)
  const hasNonEmptyContent = (node) => {
    // Text nodes with content
    if (node.type === 'text' && node.text && node.text.trim().length > 0) {
      return true;
    }

    // Image, file, or other media nodes
    if (node.type === 'image' || node.type === 'file' || node.type === 'placeholder') {
      return true;
    }

    // Recursively check children
    if (node.children && Array.isArray(node.children)) {
      return node.children.some(hasNonEmptyContent);
    }

    return false;
  };

  return editorState.root.children.some(hasNonEmptyContent);
};

/** Public alias — callers deciding "is this worth persisting / does it enable Send". */
export const draftHasContent = hasContent;

/**
 * Validate draft structure
 */
const validateDraft = (draft) => {
  if (!draft || typeof draft !== 'object') {
    console.warn('[EditorStorage] Draft is not an object');
    return false;
  }

  if (!draft.version || draft.version !== STORAGE_VERSION) {
    console.warn('[EditorStorage] Version mismatch');
    return false;
  }

  if (!draft.editorState || typeof draft.editorState !== 'object') {
    console.warn('[EditorStorage] editorState missing or invalid');
    return false;
  }

  if (!draft.timestamp || typeof draft.timestamp !== 'number') {
    console.warn('[EditorStorage] Timestamp missing or invalid');
    return false;
  }

  // Validate Lexical structure
  if (!draft.editorState.root || !draft.editorState.root.children) {
    console.warn('[EditorStorage] Missing root or children');
    return false;
  }

  // CRITICAL FIX: Reject empty editor states
  if (!hasContent(draft.editorState)) {
    console.warn('[EditorStorage] Editor state is empty, rejecting draft');
    return false;
  }

  return true;
};

/**
 * Extract blob references from Lexical editorState
 * Used to track which blob_names are referenced in the editor
 */
const extractBlobReferences = (editorState) => {
  const blobRefs = [];

  const traverseNode = (node) => {
    if (!node) return;

    // Check for image nodes
    if (node.type === 'image' && node.blob_name) {
      blobRefs.push(node.blob_name);
    }

    // Check for file nodes
    if (node.type === 'file' && node.blob_name) {
      blobRefs.push(node.blob_name);
    }

    // Recursively check children
    if (node.children && Array.isArray(node.children)) {
      node.children.forEach(traverseNode);
    }
  };

  if (editorState.root) {
    traverseNode(editorState.root);
  }

  return blobRefs;
};

/**
 * Handle storage errors
 */
const handleStorageError = (operation, error) => {
  console.error(`[EditorStorage] ${operation} failed:`, error);

  // Handle quota exceeded by triggering cleanup
  if (error.name === 'QuotaExceededError') {
    console.warn('[EditorStorage] Storage quota exceeded, triggering cleanup');
    cleanupStaleDrafts();
  }
};

/**
 * Get metadata object
 */
const getMetadata = () => {
  try {
    const raw = localStorage.getItem(METADATA_KEY);
    if (!raw) {
      return {
        version: STORAGE_VERSION,
        lastCleanup: Date.now(),
        draftKeys: []
      };
    }
    return JSON.parse(raw);
  } catch (error) {
    console.error('[EditorStorage] Failed to parse metadata:', error);
    return {
      version: STORAGE_VERSION,
      lastCleanup: Date.now(),
      draftKeys: []
    };
  }
};

/**
 * Update metadata with new draft key
 */
const updateMetadata = (key) => {
  try {
    const metadata = getMetadata();
    if (!Array.isArray(metadata.draftKeys)) {
      metadata.draftKeys = [];
    }
    if (!metadata.draftKeys.includes(key)) {
      metadata.draftKeys.push(key);
      localStorage.setItem(METADATA_KEY, JSON.stringify(metadata));
    }
  } catch (error) {
    handleStorageError('update_metadata', error);
  }
};

/**
 * Remove draft key from metadata
 */
const removeFromMetadata = (key) => {
  try {
    const metadata = getMetadata();
    metadata.draftKeys = (metadata.draftKeys || []).filter(k => k !== key);
    localStorage.setItem(METADATA_KEY, JSON.stringify(metadata));
  } catch (error) {
    handleStorageError('remove_from_metadata', error);
  }
};

/**
 * Save new chat draft
 * @param {Object} editorState - Lexical editor state as JSON
 * @param {Object} selections - User selections (project, role, mode, tools, workflows)
 * @returns {boolean} Success status
 */
export const saveNewChatDraft = (editorState, selections) => {
  try {
    // CRITICAL FIX: Don't save empty editor states
    if (!hasContent(editorState)) {
      // Clear existing draft if any
      localStorage.removeItem(NEW_CHAT_KEY);
      removeFromMetadata(NEW_CHAT_KEY);
      return true;
    }

    const blobReferences = extractBlobReferences(editorState);

    const draft = {
      version: STORAGE_VERSION,
      timestamp: Date.now(),
      editorState,
      blobReferences,
      selections
    };

    localStorage.setItem(NEW_CHAT_KEY, JSON.stringify(draft));
    updateMetadata(NEW_CHAT_KEY);
    return true;
  } catch (error) {
    handleStorageError('save_new_chat_draft', error);
    return false;
  }
};

/**
 * Load new chat draft
 * @returns {Object|null} Draft object or null if not found/invalid
 */
export const loadNewChatDraft = () => {
  try {
    const raw = localStorage.getItem(NEW_CHAT_KEY);
    if (!raw) return null;

    const draft = JSON.parse(raw);

    if (!validateDraft(draft)) {
      console.warn('[EditorStorage] Invalid draft structure, clearing');
      localStorage.removeItem(NEW_CHAT_KEY);
      return null;
    }

    return draft;
  } catch (error) {
    handleStorageError('load_new_chat_draft', error);
    localStorage.removeItem(NEW_CHAT_KEY);
    return null;
  }
};

/**
 * Clear new chat draft
 * @returns {boolean} Success status
 */
export const clearNewChatDraft = () => {
  try {
    localStorage.removeItem(NEW_CHAT_KEY);
    removeFromMetadata(NEW_CHAT_KEY);
    return true;
  } catch (error) {
    handleStorageError('clear_new_chat_draft', error);
    return false;
  }
};

/**
 * Save a conversation draft.
 *
 * An EMPTY editorState is not an error and not a no-op: it means the user cleared the composer,
 * so the stored draft is DELETED. Callers must therefore never guard this call with their own
 * "only if there is content" check — that is exactly how cleared drafts used to come back.
 *
 * Tool approvals are deliberately NOT stored here: they persist eagerly to the backend
 * (updateChatSettings), and carrying them in the draft meant a draft could also carry another
 * chat's approvals into the composer.
 *
 * @param {string} chatId - Draft scope id (chat id, or employee id on employee routes)
 * @param {Object} editorState - Lexical editor state as JSON
 * @returns {boolean} Success status
 */
export const saveChatDraft = (chatId, editorState) => {
  try {
    if (!chatId) return false;
    // The conversation was deleted — a late flush must not resurrect its draft as an orphan.
    if (purgedDraftIds.has(chatId)) return false;
    const key = chatDraftKey(chatId);

    // Empty editor state => remove the draft (see docstring).
    if (!hasContent(editorState)) {
      localStorage.removeItem(key);
      removeFromMetadata(key);
      return true;
    }

    const blobReferences = extractBlobReferences(editorState);

    const draft = {
      version: STORAGE_VERSION,
      timestamp: Date.now(),
      editorState,
      blobReferences
    };

    localStorage.setItem(key, JSON.stringify(draft));
    updateMetadata(key);
    return true;
  } catch (error) {
    handleStorageError('save_chat_draft', error);
    return false;
  }
};

/**
 * Load chat draft
 * @param {string} chatId - Draft scope id
 * @returns {Object|null} Draft object or null if not found/invalid
 */
export const loadChatDraft = (chatId) => {
  try {
    if (!chatId) return null;
    const key = chatDraftKey(chatId);
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const draft = JSON.parse(raw);

    if (!validateDraft(draft)) {
      console.warn('[EditorStorage] Invalid draft structure, clearing');
      localStorage.removeItem(key);
      removeFromMetadata(key);
      return null;
    }

    return draft;
  } catch (error) {
    handleStorageError('load_chat_draft', error);
    if (chatId) {
      localStorage.removeItem(chatDraftKey(chatId));
    }
    return null;
  }
};

/**
 * Clear chat draft.
 * Called on successful send AND when the chat/employee is deleted (CHAT_DELETED) — a draft must
 * never outlive the conversation it belongs to.
 * @param {string} chatId - Draft scope id
 * @returns {boolean} Success status
 */
export const clearChatDraft = (chatId) => {
  try {
    if (!chatId) return false;
    const key = chatDraftKey(chatId);
    localStorage.removeItem(key);
    removeFromMetadata(key);
    return true;
  } catch (error) {
    handleStorageError('clear_chat_draft', error);
    return false;
  }
};

/**
 * Permanently drop a draft because its conversation was DELETED.
 *
 * Use this — NOT clearChatDraft — on the deletion path. It also tombstones the id, so a composer
 * that is still mounted (you deleted the chat you were looking at) cannot re-create the draft on its
 * unmount flush. clearChatDraft stays the right call after a successful send, where the user may
 * legitimately type a new draft in the same chat a second later.
 *
 * @param {string} chatId - Draft scope id (chat id, or employee id on employee routes)
 * @returns {boolean} Success status
 */
export const purgeChatDraft = (chatId) => {
  if (!chatId) return false;
  purgedDraftIds.add(chatId);
  return clearChatDraft(chatId);
};

/**
 * Save projects to cache
 * @param {Array} projects - Array of project objects
 * @returns {boolean} Success status
 */
export const saveProjectsCache = (projects) => {
  try {
    const cache = {
      version: STORAGE_VERSION,
      timestamp: Date.now(),
      projects
    };
    localStorage.setItem(PROJECTS_CACHE_KEY, JSON.stringify(cache));
    return true;
  } catch (error) {
    handleStorageError('save_projects_cache', error);
    return false;
  }
};

/**
 * Load projects from cache
 * Returns null if cache is stale (>24 hours) or invalid
 * @returns {Array|null} Projects array or null
 */
export const loadProjectsCache = () => {
  try {
    const raw = localStorage.getItem(PROJECTS_CACHE_KEY);
    if (!raw) return null;

    const cache = JSON.parse(raw);

    // Validate structure
    if (!cache.version || cache.version !== STORAGE_VERSION) {
      console.warn('[EditorStorage] Invalid projects cache version, clearing');
      localStorage.removeItem(PROJECTS_CACHE_KEY);
      return null;
    }

    if (!cache.timestamp || !Array.isArray(cache.projects)) {
      console.warn('[EditorStorage] Invalid projects cache structure, clearing');
      localStorage.removeItem(PROJECTS_CACHE_KEY);
      return null;
    }

    // Check if cache is stale (>24 hours)
    const age = Date.now() - cache.timestamp;
    if (age > MAX_CACHE_AGE_MS) {
      localStorage.removeItem(PROJECTS_CACHE_KEY);
      return null;
    }

    return cache.projects;
  } catch (error) {
    handleStorageError('load_projects_cache', error);
    localStorage.removeItem(PROJECTS_CACHE_KEY);
    return null;
  }
};

/**
 * Clear projects cache
 * @returns {boolean} Success status
 */
export const clearProjectsCache = () => {
  try {
    localStorage.removeItem(PROJECTS_CACHE_KEY);
    return true;
  } catch (error) {
    handleStorageError('clear_projects_cache', error);
    return false;
  }
};

/**
 * Every draft-ish key actually present in localStorage.
 *
 * A PREFIX SCAN, not a registry read: the registry (metadata) is advisory and self-heals from this
 * scan. The old registry-only walk could never see an orphan, and getMetadata() returns an empty
 * registry on a parse error — one corrupted metadata blob used to orphan every draft forever.
 */
const scanDraftKeys = () => {
  const keys = new Set();

  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CHAT_DRAFT_PREFIX)) {
        keys.add(key);
      }
    }
    if (localStorage.getItem(NEW_CHAT_KEY)) {
      keys.add(NEW_CHAT_KEY);
    }
  } catch (error) {
    handleStorageError('scan_draft_keys', error);
  }

  // Union with the registry — covers environments where enumeration is restricted.
  const metadata = getMetadata();
  (metadata.draftKeys || []).forEach((key) => {
    if (typeof key === 'string' && (key.startsWith(CHAT_DRAFT_PREFIX) || key === NEW_CHAT_KEY)) {
      keys.add(key);
    }
  });

  return Array.from(keys);
};

/**
 * Cleanup stale drafts. Called once on app initialization (App.js) and on quota errors.
 *
 * Removes, in one prefix-scanning pass:
 *   1. the entire legacy _v1 conversation namespace (one-time migration — those drafts are
 *      presumed corrupt: cross-written by the stale-editor-reference bug, or scoped to a shift id
 *      that no longer exists),
 *   2. corrupt / version-mismatched / empty drafts,
 *   3. drafts older than MAX_DRAFT_AGE_MS,
 *   4. the oldest drafts above MAX_DRAFTS.
 * Then rebuilds the metadata registry from what actually survived.
 */
export const cleanupStaleDrafts = () => {
  try {
    const now = Date.now();
    const survivors = []; // { key, timestamp }
    let cleanedCount = 0;

    const drop = (key) => {
      localStorage.removeItem(key);
      cleanedCount += 1;
    };

    scanDraftKeys().forEach((key) => {
      try {
        // (1) legacy namespace sweep — unconditional
        if (key.startsWith(CHAT_DRAFT_PREFIX) && key.endsWith(LEGACY_CHAT_DRAFT_SUFFIX)) {
          drop(key);
          return;
        }

        const raw = localStorage.getItem(key);
        if (!raw) {
          // Key only existed in the registry
          cleanedCount += 1;
          return;
        }

        const draft = JSON.parse(raw);

        // (2) corrupt / wrong schema / empty
        if (!draft || draft.version !== STORAGE_VERSION || typeof draft.timestamp !== 'number') {
          drop(key);
          return;
        }
        if (!hasContent(draft.editorState)) {
          drop(key);
          return;
        }

        // (3) expired
        if (now - draft.timestamp > MAX_DRAFT_AGE_MS) {
          drop(key);
          return;
        }

        survivors.push({ key, timestamp: draft.timestamp });
      } catch (error) {
        // Corrupted draft, remove it
        console.warn('[EditorStorage] Removing corrupted draft:', key);
        drop(key);
      }
    });

    // (4) cap the number of CONVERSATION drafts (the /new page draft is a single fixed key)
    const chatDrafts = survivors.filter(d => d.key !== NEW_CHAT_KEY);
    if (chatDrafts.length > MAX_DRAFTS) {
      chatDrafts
        .sort((a, b) => a.timestamp - b.timestamp)
        .slice(0, chatDrafts.length - MAX_DRAFTS)
        .forEach(({ key }) => {
          drop(key);
          const idx = survivors.findIndex(s => s.key === key);
          if (idx !== -1) survivors.splice(idx, 1);
        });
    }

    if (cleanedCount > 0) {
      console.log(`[EditorStorage] Cleaned up ${cleanedCount} stale draft(s)`);
    }

    // Rebuild the registry from reality, so a corrupted registry can never orphan a key again.
    localStorage.setItem(METADATA_KEY, JSON.stringify({
      version: STORAGE_VERSION,
      lastCleanup: now,
      draftKeys: survivors.map(s => s.key)
    }));
  } catch (error) {
    console.error('[EditorStorage] cleanup_stale_drafts failed:', error);
  }
};
